import { earthCurvatureBulgeM, fresnelRadiusM } from './index.js';
import { analyzeLineOfSight, type LosDiagnostics, type LosInputSample } from './los.js';

export type HeightOptimizationMode = 'a' | 'b' | 'both' | 'minimum_total';

export interface HeightOptimizationInput {
  frequencyGHz: number;
  startGroundElevationM: number;
  endGroundElevationM: number;
  currentStartHeightM: number;
  currentEndHeightM: number;
  samples: LosInputSample[];
  mode: HeightOptimizationMode;
  targetClearancePercent: 60 | 100;
  maxStartHeightM?: number;
  maxEndHeightM?: number;
  resolutionM?: number;
  kFactor?: number;
}

export interface HeightOptimizationResult {
  mode: HeightOptimizationMode;
  targetClearancePercent: 60 | 100;
  reachable: boolean;
  currentStartHeightM: number;
  currentEndHeightM: number;
  recommendedStartHeightM: number;
  recommendedEndHeightM: number;
  startIncreaseM: number;
  endIncreaseM: number;
  achievedClearancePercent: number;
  criticalPointDistanceM: number;
  method: 'analytical' | 'convex-search';
  iterations: number;
  reason: string | null;
}

interface HeightConstraint { startWeight: number; endWeight: number; deficitM: number }

const DEFAULT_MAX_HEIGHT_M = 200;
const DEFAULT_RESOLUTION_M = 0.1;
const SEARCH_ITERATION_LIMIT = 96;

function validate(input: HeightOptimizationInput): void {
  if (input.samples.length < 3) throw new Error('Height optimization requires at least one interior profile sample');
  if (input.frequencyGHz <= 0) throw new RangeError('Frequency must be positive');
  if (input.currentStartHeightM < 0 || input.currentEndHeightM < 0) throw new RangeError('Current heights cannot be negative');
  if ((input.resolutionM ?? DEFAULT_RESOLUTION_M) <= 0) throw new RangeError('Resolution must be positive');
}

function constraints(input: HeightOptimizationInput): HeightConstraint[] {
  const totalDistanceM = input.samples[input.samples.length - 1]?.distanceM ?? 0;
  if (totalDistanceM <= 0) throw new RangeError('Profile distance must be positive');
  const startElevationM = input.startGroundElevationM + input.currentStartHeightM;
  const endElevationM = input.endGroundElevationM + input.currentEndHeightM;
  return input.samples.slice(1, -1).map((sample) => {
    const endWeight = sample.distanceM / totalDistanceM;
    const startWeight = 1 - endWeight;
    const lineElevationM = startElevationM * startWeight + endElevationM * endWeight;
    const distanceToEndM = totalDistanceM - sample.distanceM;
    const correctedSurfaceM = sample.effectiveSurfaceM + earthCurvatureBulgeM(sample.distanceM, distanceToEndM, input.kFactor ?? 4 / 3);
    const requiredElevationM = correctedSurfaceM + fresnelRadiusM(input.frequencyGHz, sample.distanceM, distanceToEndM) * input.targetClearancePercent / 100;
    return { startWeight, endWeight, deficitM: Math.max(0, requiredElevationM - lineElevationM) };
  });
}

function roundUp(value: number, resolutionM: number): number {
  return Math.ceil(Math.max(0, value - 1e-9) / resolutionM) * resolutionM;
}

function diagnostics(input: HeightOptimizationInput, startIncreaseM: number, endIncreaseM: number): LosDiagnostics {
  return analyzeLineOfSight({
    frequencyGHz: input.frequencyGHz,
    startAntennaElevationM: input.startGroundElevationM + input.currentStartHeightM + startIncreaseM,
    endAntennaElevationM: input.endGroundElevationM + input.currentEndHeightM + endIncreaseM,
    samples: input.samples,
    kFactor: input.kFactor ?? 4 / 3,
    requiredClearancePercent: input.targetClearancePercent,
  });
}

function requiredEndIncrease(items: HeightConstraint[], startIncreaseM: number): number {
  return Math.max(0, ...items.map((item) => (item.deficitM - item.startWeight * startIncreaseM) / item.endWeight));
}

function minimumTotalIncreases(items: HeightConstraint[], maxStartIncreaseM: number, maxEndIncreaseM: number, resolutionM: number): { start: number; end: number; reachable: boolean; iterations: number } {
  if (requiredEndIncrease(items, maxStartIncreaseM) > maxEndIncreaseM + 1e-8) {
    return { start: maxStartIncreaseM, end: maxEndIncreaseM, reachable: false, iterations: 0 };
  }
  let feasibleLow = 0;
  let feasibleHigh = maxStartIncreaseM;
  let iterations = 0;
  if (requiredEndIncrease(items, feasibleLow) > maxEndIncreaseM) {
    while (feasibleHigh - feasibleLow > resolutionM / 16 && iterations < SEARCH_ITERATION_LIMIT) {
      const middle = (feasibleLow + feasibleHigh) / 2;
      if (requiredEndIncrease(items, middle) <= maxEndIncreaseM) feasibleHigh = middle;
      else feasibleLow = middle;
      iterations += 1;
    }
    feasibleLow = feasibleHigh;
  }
  let low = feasibleLow;
  let high = maxStartIncreaseM;
  while (high - low > resolutionM / 16 && iterations < SEARCH_ITERATION_LIMIT) {
    const first = low + (high - low) / 3;
    const second = high - (high - low) / 3;
    const firstTotal = first + requiredEndIncrease(items, first);
    const secondTotal = second + requiredEndIncrease(items, second);
    if (firstTotal <= secondTotal) high = second;
    else low = first;
    iterations += 1;
  }
  const candidates = [feasibleLow, low, (low + high) / 2, high, maxStartIncreaseM]
    .map((start) => ({ start, end: requiredEndIncrease(items, start) }))
    .filter((candidate) => candidate.end <= maxEndIncreaseM + 1e-8);
  const best = candidates.reduce((selected, candidate) => candidate.start + candidate.end < selected.start + selected.end ? candidate : selected);
  return { start: roundUp(best.start, resolutionM), end: roundUp(best.end, resolutionM), reachable: true, iterations };
}

export function optimizeAntennaHeights(input: HeightOptimizationInput): HeightOptimizationResult {
  validate(input);
  const resolutionM = input.resolutionM ?? DEFAULT_RESOLUTION_M;
  const maxStartHeightM = input.maxStartHeightM ?? DEFAULT_MAX_HEIGHT_M;
  const maxEndHeightM = input.maxEndHeightM ?? DEFAULT_MAX_HEIGHT_M;
  if (maxStartHeightM < input.currentStartHeightM || maxEndHeightM < input.currentEndHeightM) throw new RangeError('Maximum height cannot be below the current height');
  const items = constraints(input);
  const maxStartIncreaseM = maxStartHeightM - input.currentStartHeightM;
  const maxEndIncreaseM = maxEndHeightM - input.currentEndHeightM;
  let startIncreaseM = 0;
  let endIncreaseM = 0;
  let reachable = true;
  let method: HeightOptimizationResult['method'] = 'analytical';
  let iterations = 1;

  if (input.mode === 'a') {
    startIncreaseM = roundUp(Math.max(0, ...items.map((item) => item.deficitM / item.startWeight)), resolutionM);
    reachable = startIncreaseM <= maxStartIncreaseM + 1e-8;
    startIncreaseM = Math.min(startIncreaseM, maxStartIncreaseM);
  } else if (input.mode === 'b') {
    endIncreaseM = roundUp(Math.max(0, ...items.map((item) => item.deficitM / item.endWeight)), resolutionM);
    reachable = endIncreaseM <= maxEndIncreaseM + 1e-8;
    endIncreaseM = Math.min(endIncreaseM, maxEndIncreaseM);
  } else if (input.mode === 'both') {
    const sharedIncreaseM = roundUp(Math.max(0, ...items.map((item) => item.deficitM)), resolutionM);
    reachable = sharedIncreaseM <= Math.min(maxStartIncreaseM, maxEndIncreaseM) + 1e-8;
    startIncreaseM = Math.min(sharedIncreaseM, maxStartIncreaseM, maxEndIncreaseM);
    endIncreaseM = startIncreaseM;
  } else {
    method = 'convex-search';
    const result = minimumTotalIncreases(items, maxStartIncreaseM, maxEndIncreaseM, resolutionM);
    startIncreaseM = result.start;
    endIncreaseM = result.end;
    reachable = result.reachable;
    iterations = result.iterations;
  }

  const resultDiagnostics = diagnostics(input, startIncreaseM, endIncreaseM);
  reachable = reachable && resultDiagnostics.minimumFresnelClearancePercent + 1e-7 >= input.targetClearancePercent;
  return {
    mode: input.mode,
    targetClearancePercent: input.targetClearancePercent,
    reachable,
    currentStartHeightM: input.currentStartHeightM,
    currentEndHeightM: input.currentEndHeightM,
    recommendedStartHeightM: input.currentStartHeightM + startIncreaseM,
    recommendedEndHeightM: input.currentEndHeightM + endIncreaseM,
    startIncreaseM,
    endIncreaseM,
    achievedClearancePercent: resultDiagnostics.minimumFresnelClearancePercent,
    criticalPointDistanceM: resultDiagnostics.criticalPoint.distanceM,
    method,
    iterations,
    reason: reachable ? null : `Целевая очистка F1 ${input.targetClearancePercent}% недостижима при заданных максимальных высотах`,
  };
}
